import { cubicEaseOut } from '@helpers/animate';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { contentStyles, link } from '@helpers/typography';
import svgCheckboxTick from '@img/icons/checkbox-tick.svg?url';
import svgFieldInvalid from '@img/icons/field-invalid.svg?url';
import svgFieldValid from '@img/icons/field-valid.svg?url';
import svgChevronDown from '@img/icons/select-icon.svg?url';
import styled, { css } from 'styled-components';
const Container = styled.section `
  --inputHeight: 33px;
  display: block;

  @media ${from(Device.TabletLarge)} {
    --inputHeight: 58px;
  }
`;
const Form = styled.form `
  display: block;
`;
const Fieldset = styled.fieldset `
  display: block;

  & + & {
    margin: 80px 0 0;
  }
`;
const Legend = styled.legend `
  display: block;
  ${fonts.helvetica.bold};
  font-size: 22px;
  line-height: 30px;
  margin: 0 0 12px;
  text-transform: capitalize;
`;
const Field = styled.div `
  position: relative;
  display: block;
  margin: 0;
  padding-right: 28px;

  &:not(:last-child) {
    margin-bottom: 46px;
  }

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    display: block;
    height: 20px;
    background-position: center right;
    background-repeat: no-repeat;
    background-size: 20px 20px;
    transform: translateY(-50%) translateY(calc(var(--inputHeight) / 2));
    width: 20px;
  }

  &[data-invalid='true']::before {
    content: '';
    background-image: url('${svgFieldInvalid}');
  }

  [data-submitted='true'] &[data-invalid='false']::before {
    content: '';
    background-image: url('${svgFieldValid}');
  }

  @media ${from(Device.TabletLarge)} {
    padding-right: 44px;

    &:not(:last-child) {
      margin-bottom: 56px;
    }
  }
`;
const Line = styled.span `
  position: absolute;
  bottom: 0;
  left: 0;
  display: block;
  background: var(--fgColor);
  height: 2px;
  transition: background-color;
  transition-duration: 0.3s;
  transition-timing-function: ${cubicEaseOut};
  width: calc(100% - 28px);

  [data-submitted='true'] [data-invalid='false'] & {
    background-color: ${brand.validation.valid};
  }

  [data-invalid='true'] & {
    background-color: ${brand.validation.invalid};
  }

  @media ${from(Device.TabletLarge)} {
    width: calc(100% - 44px);
  }
`;
const Label = styled.label `
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  ${fonts.helvetica.regular};
  font-size: 16px;
  height: var(--inputHeight);
  letter-spacing: -0.01em;
  line-height: 1em;
  margin: 0;
  pointer-events: none;
  transform: scale(1) translateY(-50%) translateY(calc(var(--inputHeight) / 2));
  transform-origin: top left;
  transition: transform 0.3s ${cubicEaseOut};
  will-change: transform;

  [data-dirty='true'] & {
    transform: scale(0.8) translateY(-50%);
  }

  @media ${from(Device.TabletLarge)} {
    font-size: 18px;
  }
`;
const StaticLabel = styled.label `
  display: flex;
  align-items: center;
  ${fonts.helvetica.regular};
  font-size: 16px;
  height: var(--inputHeight);
  letter-spacing: -0.01em;
  line-height: 1em;
  margin: 0 0 18px;
  pointer-events: none;

  @media ${from(Device.TabletLarge)} {
    font-size: 18px;
  }
`;
const ControlLabel = styled.label `
  display: flex;
  align-items: center;
  ${fonts.helvetica.regular};
  font-size: 16px;
  line-height: 22px;
  margin: 0 0 12px;
  min-height: var(--inputHeight);
  gap: 8px;

  > svg {
    height: 18px;
    width: 18px;
  }

  > input {
    ${srOnly};
  }
`;
const CheckboxLabel = styled.span `
  position: relative;
  padding-left: 52px;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    background-position: center;
    background-repeat: no-repeat;
    background-size: 18px 18px;
    box-sizing: border-box;
    height: 32px;
    width: 32px;
  }

  &::after {
    content: '';
    background-image: url(${svgCheckboxTick});
    font-size: 16px;
    height: 32px;
    line-height: 16px;
    opacity: 0;
    text-align: center;
    width: 32px;
    transition: opacity 0.15s ease-out;
  }

  &::before {
    border: 2px solid var(--fgColor);
  }

  input:checked + & {
    &::after {
      opacity: 1;
    }
  }

  [data-submitted='true'] [data-invalid='false'] &::before {
    border-color: ${brand.validation.valid};
  }

  [data-invalid='true'] &::before {
    border-color: ${brand.validation.invalid};
  }
`;
const RadioLabel = styled.span `
  position: relative;
  padding-left: 32px;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    border-radius: 50%;
    height: 16px;
    width: 16px;
    transform: translateY(-50%) translateY(calc(var(--inputHeight) / 2));
  }

  &::after {
    content: '✓';
    font-size: 10px;
    height: 18px;
    line-height: 16px;
    opacity: 0;
    text-align: center;
    width: 18px;
    transition: opacity 0.15s ease-out;
  }

  &::before {
    border: 1px solid var(--fgColor);
  }

  input:checked + & {
    &::after {
      opacity: 1;
    }
  }

  [data-submitted='true'] [data-invalid='false'] &::before {
    border-color: ${brand.validation.valid};
  }

  [data-invalid='true'] &::before {
    border-color: ${brand.validation.invalid};
  }
`;
const MandatoryIndicator = styled.span `
  [data-indicator-field='1'] & {
    color: ${brand.validation.invalid};
  }
`;
const IndicatorMessage = styled.div `
  [data-indicator-field='1'] & {
    color: ${brand.validation.invalid};
  }

  span {
    font-style: italic;
  }
`;
const sharedInputStyles = css `
  display: block;
  appearance: none !important;
  background: var(--bgColor);
  border: 0;
  color: var(--fgColor);
  ${fonts.helvetica.regular};
  font-size: 16px;
  height: var(--inputHeight);
  letter-spacing: -0.01em;
  line-height: 20px;
  padding: 8px 16px 8px 0;
  transition: border-color 0.15s ease-out;
  width: 100%;

  @media ${from(Device.TabletLarge)} {
    font-size: 18px;
    line-height: 30px;
  }
`;
const Input = styled.input `
  ${sharedInputStyles};
`;
const Select = styled.select `
  ${sharedInputStyles};
  appearance: none !important;
  background: url('${svgChevronDown}') center right 12px no-repeat;
  background-size: 11px 9px;

  option {
    background-color: var(--bgColor);
    color: var(--fgColor);
  }

  @media ${from(Device.TabletLarge)} {
    background-size: 17px 14px;
  }
`;
const Textarea = styled.textarea `
  ${sharedInputStyles};
  height: auto;
  min-height: calc(var(--inputHeight) * 2);
`;
const Error = styled.span `
  position: absolute;
  bottom: 0;
  left: 0;
  display: block;
  color: ${brand.validation.invalid};
  ${fonts.helvetica.regular};
  font-size: 14px;
  line-height: 16px;
  margin: 0;
  padding: 0;
  transform: translateY(100%) translateY(8px);
`;
const Submit = styled.div `
  display: flex;
  align-items: center;
  flex-direction: row;
  margin: 46px 0 0;

  @media ${from(Device.TabletLarge)} {
    margin-top: 56px;
  }
`;
const SubmitIndicator = styled.div `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 16px;
`;
const ConfirmationMessage = styled.p `
  display: block;
  ${fonts.helvetica.light};
  font-size: 22px;
  line-height: 34px;
  margin: 0 0 12px;
`;
const ErrorList = styled.ul `
  display: block;
  color: ${brand.validation.invalid};
  margin-bottom: 46px;

  @media ${from(Device.TabletLarge)} {
    margin-bottom: 56px;
  }
`;
const ErrorListItem = styled.li `
  display: block;
  ${fonts.helvetica.regular};
  font-size: 16px;
  line-height: 22px;
  margin: 0 0 12px;
`;
const FieldTitle = styled.h2 `
  display: block;
  ${fonts.helvetica.regular};
  font-size: 16px;
  line-height: 26px;
  margin: 0;
`;
const FieldDescription = styled.p `
  ${contentStyles}
  display: block;
  ${fonts.helvetica.regular};
  font-size: 14px;
  line-height: 26px;
  margin: 0;
`;
// File Upload Styles
const FileUploadLabelContainer = styled.div `
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  overflow: hidden;

  > label {
    ${link};

    text-decoration: none;
    margin: 0;
    white-space: nowrap;

    &:hover {
      cursor: pointer;
    }
  }
`;
const FileNameContent = styled.span `
  display: block;
  font-size: 14px;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${brand.grey.grey5};
  max-width: 180px;

  @media ${from(Device.TabletLarge)} {
    font-size: 16px;
    max-width: 250px;
  }
`;
const HiddenLabel = styled.span `
  ${srOnly}
`;
const HiddenInput = styled.input `
  ${srOnly}
`;
export default {
    CheckboxLabel,
    ConfirmationMessage,
    Container,
    ControlLabel,
    Error,
    ErrorList,
    ErrorListItem,
    Field,
    FieldDescription,
    FieldTitle,
    Fieldset,
    Form,
    Input,
    Label,
    MandatoryIndicator,
    IndicatorMessage,
    Legend,
    Line,
    RadioLabel,
    Select,
    StaticLabel,
    Submit,
    SubmitIndicator,
    Textarea,
    FileNameContent,
    HiddenLabel,
    HiddenInput,
    FileUploadLabelContainer,
};
